import type { ProtoMetricCategory, ProtoPlayerStats } from "@catbento/shared";
import type { RawPlayerRecord } from "./snapshot.js";
// The snapshot's own category config, copied out once (step 1, the no-snapshot build): it's
// fixed config, not data, and live mode must not download the snapshot to get it. The snapshot
// never changes, so the copy can't drift. (The API's /player-metric-categories is the engine's
// list — a different shape and wording — so it isn't a drop-in.)
import metricCategories from "./metric-categories.json";

// Pure lookups for player stats (Phase 02b). Kept separate from the hooks so the
// season-resolution logic is unit-tested without a DOM. Reproduces exactly what
// renderers-stats.js reads: the category config from metricCategories, and a
// player's flat stats map from seasons[code].stats.

type Players = Record<string, RawPlayerRecord>;

/**
 * The stat category config (appearance/passing/attacking/defending/goalkeeping),
 * as the snapshot has it. The renderer picks a category by id and walks its
 * subcategories → playerMetrics. Both sites, both modes.
 */
export function getMetricCategories(): ProtoMetricCategory[] {
  return metricCategories as ProtoMetricCategory[];
}

/**
 * A player's stats for a season. Season resolution matches the renderer: use the
 * requested `season` when the player actually has it, else fall back to
 * `currentSeason`. Season codes are sorted newest-first (string-desc, e.g.
 * "52025" before "52024"), exactly as the prototype's season selector lists them.
 * Returns null if the name isn't in the snapshot.
 */
export function getPlayerStats(
  players: Players,
  name: string,
  season?: string
): ProtoPlayerStats | null {
  const p = players[name];
  if (!p) return null;

  const seasons = p.seasons ?? {};
  const seasonCodes = Object.keys(seasons).sort((a, b) => b.localeCompare(a));
  const currentSeason = p.currentSeason ?? seasonCodes[0] ?? "";
  const seasonCode = season && seasons[season] ? season : currentSeason;
  const stats = seasons[seasonCode]?.stats ?? {};

  return {
    seasonCodes,
    seasonCode,
    currentSeason,
    stats,
    positionTemplate: p.positionTemplate ?? null,
  };
}
